// @file Common types that we use
import type { UserRole } from '@@/bits/user_model'
import type { Invoice, JsonAPIResource, Reaction, WallBackgroundType } from '@padlet/arvo'
import type {
  ApiErrorCode,
  CommandLevel,
  ContentModerationCategory,
  ContentModerationMode,
  LibraryMembershipRole,
  LibraryType,
  ModerationCategories,
  NotificationChannelType,
  NotificationType,
  SchoolPermissionString,
  SchoolStudentPermissionBoolean,
  SchoolTeacherPermissionBoolean,
  UserAccountType,
  WallAccessPrivacyType,
  WallAccessRight,
  WallCollaboratorStatus,
  WallPermission,
  WallPrivacy,
  WallPrivacyOption,
  WallVizName,
} from 'enums'

type Id = number
type UserId = Id
type UserGroupId = Id
type TenantId = Id
type WallId = Id
type FolderId = Id
type LibraryId = Id
type LibraryMembershipId = Id
type PrivacyPolicyId = Id
type OnboardingFlowId = Id

interface DeviceDetails {
  // Gadgets
  ipad: boolean
  ipod: boolean
  iphone: boolean
  iphoneX: boolean

  // OSes
  ios: boolean
  android: boolean
  windowsPhone: boolean
  mac: boolean
  windows: boolean
  webos: boolean
  blackberry: boolean
  windowsTouch: boolean

  // Browsers
  edge: boolean
  chrome: boolean
  firefox: boolean
  ie: boolean
  webkit: boolean
  safari: boolean
  ie11: boolean
  ie10: boolean
  version: number

  // Platform type
  mobile: boolean
  desktop: boolean
  supported: boolean

  // Capabilities
  touchable: boolean
  mousable: boolean
  retina: boolean
  clickGesture: string
  dragFile: boolean
  cookies: boolean

  // Variants
  tablet: boolean
  phone: boolean
  iosApp: boolean
  androidApp: boolean
  app: boolean
  desktopApp: boolean
  nwjs: boolean
  electronApp: boolean
  zoomApp: boolean

  // Push
  safariPush: boolean
  webPush: boolean
  browserPush: boolean
}

interface UserQuota {
  walls_used: number
  walls_limit: number
  advertized_walls_limit: number
  can_make: boolean
}

interface User {
  id: UserId
  tenant_id: TenantId
  paying: boolean
  quota: UserQuota
  role: UserRole
  lang: string
  created_at: Date
  registered_at: Date | null
  defaultAvatar?: string
  avatar?: string
  paying_for_self_only?: boolean
  membership_tier?: string
  period?: string | null
  name?: string
  username?: string
  display_name?: string
  email?: string
  short_name?: string
  registered?: boolean
  profile_url?: string
  bio?: string
  isBetaFeaturePreviewEnabled?: boolean
  can_show_email?: boolean
  canUpdateEmail?: boolean
  canUpdateName?: boolean
  canUpdateUsername?: boolean
  tenant_type?: string
  hasRecentPadlets?: boolean
  defaultLibraryId?: string
  visible?: boolean
  // Specifically used to trigger snackbar when redirected to dashboard settings after changing and verifying email
  isEmailRecentlyUpdatedAndVerified?: boolean
  // Specifically used to trigger snackbar when redirected to dashboard after login and verifying email
  isEmailRecentlyLoggedInAndVerified?: boolean
  // true if user's email address is verified, or if the user logs in via SSO
  is_verified?: boolean
  account_type?: UserAccountType
}

interface UserCamelCase {
  id: UserId
  tenant_id: TenantId
  paying: boolean
  quota: UserQuota
  role: UserRole
  lang: string
  createdAt: Date
  registeredAt: Date | null
  defaultAvatar?: string
  avatar?: string
  payingForSelfOnly?: boolean
  membershipTier?: string
  period?: string | null
  name?: string
  username?: string
  displayName?: string
  email?: string
  shortName?: string
  registered?: boolean
  profileUrl?: string
  bio?: string
  isBetaFeaturePreviewEnabled?: boolean
  canShowEmail?: boolean
  canUpdateEmail?: boolean
  canUpdateName?: boolean
  canUpdateUsername?: boolean
  tenantType?: string
  hasRecentPadlets?: boolean
  defaultLibraryId?: string
  visible?: boolean
  // Specifically used to trigger snackbar when redirected to dashboard settings after changing and verifying email
  isEmailRecentlyUpdatedAndVerified?: boolean
  // Specifically used to trigger snackbar when redirected to dashboard after login and verifying email
  isEmailRecentlyLoggedInAndVerified?: boolean
  // true if user's email address is verified, or if the user logs in via SSO
  isVerified?: boolean
  accountType?: UserAccountType
  isNative?: boolean
}

interface UserWithHashId extends User {
  hashid: string
}

interface UserOptions {
  canAddLibrary?: boolean
  canManagePeople?: boolean
  canMakeInLibrary?: boolean
  canMakeInTenant?: boolean
  canViewGallery?: boolean
  linkedGoogleDriveEmail?: string
}

type AccountType = 'user' | 'library'
type AccountKey = { type: 'library'; id: LibraryId } | { type: 'user'; id: UserId }
type SharedCollectionAccountKey = AccountKey

interface TenantAssets {
  home_logo?: string
}

interface Tenant {
  id: TenantId
  assets: Partial<TenantAssets> | null
  domain_name: string
  membership: string
  name: string
  type?: string
  alternate_user_domains?: string
  auto_create_accounts?: boolean
  invalid_email_domains?: string[]
  owner_email_domain?: string | null
  enable_google_login?: boolean | null
  enable_office365_login?: boolean | null
  enable_classlink_login?: boolean | null
  enable_saml_login?: boolean
  classlink_tenant_id?: string | null
  is_lti_ready?: boolean
  is_native?: boolean
  ownerLink: string
  hide_password_login_if_sso_enabled: boolean
  settings?: {
    permissions?: {
      // student wall permissions
      studentDefaultWallPrivacy?: WallPrivacy
      studentDefaultWallPermission?: WallPermission
      canStudentsMakeWalls?: boolean
      canStudentsMakePrivateWalls?: boolean
      canStudentsMakeOrgWideHiddenWalls?: boolean
      canStudentsMakeOrgWideDashboardWalls?: boolean
      canStudentsMakePasswordWalls?: boolean
      canStudentsMakeSecretWalls?: boolean
      // teacher wall permissions
      teacherDefaultWallPrivacy?: WallPrivacy
      teacherDefaultWallPermission?: WallPermission
      canTeachersMakeWalls?: boolean
      canTeachersMakePrivateWalls?: boolean
      canTeachersMakeOrgWideHiddenWalls?: boolean
      canTeachersMakeOrgWideDashboardWalls?: boolean
      canTeachersMakePasswordWalls?: boolean
      canTeachersMakeSecretWalls?: boolean
      // student permission
      canStudentsViewGallery?: boolean
      // teacher permissions
      canTeachersAddNewUsers?: boolean
      canTeachersEditUsers?: boolean
      // briefcase permissions
      briefcaseDefaultWallPrivacy?: WallPrivacy
      briefcaseDefaultWallPermission?: WallPermission
    }
  }
}

type TenantParamsKeys =
  | 'assets'
  | 'domain_name'
  | 'name'
  | 'alternate_user_domains'
  | 'auto_create_accounts'
  | 'enable_google_login'
  | 'enable_office365_login'
  | 'enable_classlink_login'
  | 'hide_password_login_if_sso_enabled'
  | 'classlink_tenant_id'

type TenantParams = Pick<Tenant, TenantParamsKeys>

interface TenantInviteLinks {
  id?: TenantId
  studentLink?: string
  teacherLink?: string
  adminLink?: string
  isStudentLinkCodeEnabled?: boolean
  isTeacherLinkCodeEnabled?: boolean
  isAdminLinkCodeEnabled?: boolean
}

interface TenantInviteLinksParams {
  generateStudentLinkCode: boolean
  generateTeacherLinkCode: boolean
  generateAdminLinkCode: boolean
  isStudentLinkCodeEnabled: boolean
  isTeacherLinkCodeEnabled: boolean
  isAdminLinkCodeEnabled: boolean
}

interface TenantAnalytics {
  orgAnalyticsEmbedLink: string
  monthlyAnalyticsEmbedLink: string | null
  orgUserAnalyticsEmbedLink: string
}

interface DomainValidation {
  result: boolean
  subdomain?: string
  code?: ApiErrorCode
}

type WallViz = 'free' | 'grid' | 'matrix' | 'shelf' | 'stream' | 'map' | 'timeline' | 'table' | 'timeline_v2'

type WallVizNameType = keyof typeof WallVizName

interface OnboardingWallViz {
  name: WallVizNameType
  value: WallViz
  imagePath: string
  sectionsImagePath: string
}

interface WallBackground {
  url: string
  thumbnail_url: string
  palette_order: number
  width: number
  height: number
  fill: 'cover' | 'tile'
  effects: string[]
  is_full_page: boolean
  luminance_name: 'light' | 'dark'
  dominant_color: number[]
  dominant_color_as_rgb: string
  primary_color: number[] | null
  primary_color_as_rgb: string | null
  primary_text_color: 'white' | 'black' | null
  accent_color: number[] | null
  accent_color_as_rgb: string | null
  accent_text_color: 'white' | 'black' | null
  is_custom?: boolean
  name?: string
  // for backward compatibility
  effect?: string | null
  is_title_bar_transparent_by_default?: boolean
  is_title_bar_transparent?: boolean
  type: WallBackgroundType
}

interface Wall {
  id: WallId
  library_id?: LibraryId | null
  name?: string
  description?: string
  namespace?: string
  address: string
  archived_at: string | null
  created_at: string
  updated_at: string
  last_present_at?: string | null
  days_till_deletion?: number | null
  title: string
  viz: WallViz
  collaborator_right: number
  portrait?: string
  post_size?: PostSize
  builder: {
    id: UserId
    avatar: string
    display_name: string
    username: string
    name?: string
  }
  background: WallBackground
  thumbnail: {
    dominant_color: number[]
    dominant_color_as_rgb: string
    height: number
    width: number
    url: string
  }
  links: {
    show: string
    permanent: string
  }
}

interface WallPostProperties {
  subject_placeholder: string | null
  body_placeholder: string | null
  subject_enabled: boolean
  attachment_settings: boolean | Record<string, any>
  body_enabled: boolean
  color_enabled: boolean
  location_enabled: boolean
}

interface WallCustomPostProperty {
  id?: string // id of a custom property is a random string not a number
  name: string
  data_type: 'text' | 'date'
  sort_index: number
}

interface WallTemplate {
  id: WallId
  name?: string
  description?: string
  title: string
  thumbnail: {
    height: number
    width: number
    url: string
    dominantColor: number[]
    dominantColorAsRgb: string
  }
}

interface WallGalleryTemplate {
  id: WallId
  name?: string
  description?: string
  title: string
  thumbnail: {
    height: number
    width: number
    url: string
    dominantColor: number[]
    dominantColorAsRgb: string
  }
  galleryTemplateId: string
  category?: string[]
  audience: string
}

interface WallCamelCase {
  id: WallId
  description?: string
  libraryId: LibraryId | null
  name: string
  namespace: string
  address: string
  archivedAt: string | null
  createdAt: string
  updatedAt: string
  lastPresentAt: string | null
  daysTillDeletion?: number | null
  title: string
  viz: WallViz
  collaboratorRight: number
  isInRecents: boolean
  builder: {
    id: UserId
    avatar: string
    displayName: string
    username: string
  }
  background: {
    dominantColor: number[]
    dominantColorAsRgb: string
    fill: string
    height?: number
    width?: number
    url: string
    effect: string | null
    luminanceName: 'light' | 'dark'
  }
  thumbnail: {
    dominantColor: number[]
    dominantColorAsRgb: string
    height: number
    width: number
    url: string
  }
  links: {
    show: string
    permanent: string
  }
  isTemplate?: boolean
}

export enum PostSize {
  STANDARD = 'standard',
  WIDE = 'wide',
}

type ReactionType = 'like' | 'vote' | 'star' | 'grade' | 'none'
interface WallReactionData {
  type: ReactionType
  max_value?: number
}
interface Folder {
  id: FolderId
  name: string
  // TODO ENG-4489 remove snake case field after removing gql client
  walls_count?: number
  wallsCount?: number
  icon?: string
}

interface UserGroup {
  id: UserGroupId
  name: string
}

interface Theme {
  id: number
  name: string
  full_name: string
  color_scheme: 'light' | 'dark'
  font_id: number
}

interface PinDropSuggestion {
  locationName: string
  placeId: string
  types?
  zoom: number
}

type Cid = string
type PostColor = 'red' | 'orange' | 'green' | 'blue' | 'purple' | undefined | null
interface PostPositionMap {
  top?: number
  left?: number
}

interface Point {
  longitude: number
  latitude: number
}

export interface PollChoice {
  choice_text: string
  poll_choice_id: Id
  total_votes: number
  user_voted: boolean
}

export interface PollResult extends PollChoice {
  percentage: number
  pollWinner: boolean
}

export interface Poll {
  type: 'poll'
  poll_id: Id
  user_voted?: boolean
  question: string
  poll_choices: PollChoice[]
  closed_at?: string | null
  voter_id: Id
}

// Attachment props can have various formats.
// We can be flexible for now until we have
// a better idea of how it should looks like.
type AttachmentProps = Record<string, any>

interface WishContent {
  attachment_props: AttachmentProps
  is_processed?: boolean
}

interface PostAttributes {
  cid?: Cid
  id?: Id
  subject?: string
  body?: string
  attachment?: string
  attachment_caption?: string | null
  file?: File | null
  published?: boolean
  width?: number
  wallSectionId?: number
  sortIndex?: number
  wall_section_id?: number
  created_at?: Date | string
  updated_at?: Date | string
  content_updated_at?: Date | string
  author_id?: number
  author?: User
  wall_id?: number
  permalink?: string
  sort_index?: number
  rank?: string
  position?: PostPositionMap
  top?: number
  left?: number
  color?: PostColor
  row_id?: number
  col_id?: number
  rowCid?: string
  colCid?: string
  row_span?: number
  col_span?: number
  locationPoint?: Point
  location_point?: Point
  locationName?: string
  location_name?: string
  is_content_hidden?: boolean
  wish_content?: WishContent
  scheduled_at?: string
  published_at?: string
  moderation_violations?: ModerationCategories[]
  pinned_at?: string | null
  custom_properties?: PostCustomProperties | null
}

interface PostCustomProperties {
  [id: string]: string | undefined // TODO: this type will be expanded over time
}

interface UpdatePostArgs extends PostAttributes {
  wish_order?: { previous: Id; next: Id } | Id[]
  gap_size?: number | null
}

interface MapPostAttributes extends PostAttributes {
  location_point: Point
  location_name: string
}

interface FileAttributes {
  name: string
  size: number | null
  isGoogleDriveFile: boolean
}

interface UpdateDraftAttributes extends PostAttributes {
  draftCreatedAt?: number
  minimizedPlaceholderTitle?: string
  uploadingFile?: File | FileAttributes | null
  uploadProgress?: number
  maxFileUploadSize?: number
  shouldAutoPublish?: boolean
}

interface Post extends PostAttributes {
  cid: Cid
}

interface DraftPost extends Post {
  // timestamp generated using Date.now()
  draftCreatedAt?: number
  minimizedPlaceholderTitle?: string
  uploadingFile?: File | null
  uploadProgress?: number
  maxFileUploadSize?: number
  shouldAutoPublish?: boolean
}

type CommentTempId = string
type CommentId = Id | CommentTempId
interface Comment {
  id: CommentId
  wish_id: Id
  user_id?: Id
  body: string
  html_body?: string
  user: User
  created_at: string
  updated_at: string
  moderation_violations?: ModerationCategories[]
  is_awaiting_approval?: boolean
  published_at?: string
  submitted_at?: string
}

interface Section {
  id: Id
  title: string
  color?: string
  sort_index?: number
  created_at?: string
  updated_at?: string
}

interface PostConnection {
  id: Id
  label: string
  from_wish_id: Id
  to_wish_id: Id
}

export type ModalSize = 'default' | 'wide' | 'extra-wide'

// Personal  plans
interface Plan {
  id: string
  name: string
  period: 'monthly' | 'annual'
  price: string
  formattedPrice: string
  currency: string
  tier: 'platinum' | 'gold'
  is_legacy: boolean
  url: string
  annual_savings_percentage: number
}

interface AccumulatedReactionData {
  sum: number
  totalReactionsCount: number
  reactionType: ReactionType
  userReaction?: Reaction
}
type AccumulatedReactions = Record<Id, AccumulatedReactionData | undefined>

// Estimated cancellation refund
interface EstimateCancellationRefundResult {
  amount: string
  currency: string
}

// Library plans
type LibraryPlan = Plan

// Library invoices
type LibraryInvoice = Invoice

// Backpack plans
interface BackpackPlanPrices {
  '10'?: number
  '15'?: number
  '25'?: number
  '35'?: number
  '50'?: number
  '75'?: number
  '100'?: number
  '125'?: number
}

interface BackpackPlanResult {
  prices: BackpackPlanPrices
  currency: string
}

interface GoogleDriveAuthorizationResult {
  linkedEmail: string | null
  accessToken: string | null
}

// Estimated downgrade to Team Neon refund
interface EstimateTeamNeonDowngradeRefund {
  amount: string
  currency: string
}

// Library Change card URL
interface ChangeCardUrl {
  change_card_url: string
}

// Downgrade to Team Neon result
interface LibraryDowngradeToTeamNeonResult {
  status_link: string
}

// Downgrade to Team Neon result
interface LibrarySwitchResult {
  status_link: string
}

interface LibrarySwitchStatusResult {
  switch_status: {
    is_switched: boolean
    is_refunded?: boolean
    error_message?: string
  }
}

// Result from /billing/pause, /billing/resume, /billing/cancel_pausing
// which is the result (i.e. fields in billing to update) for the operations
// @see @padlet/arvo for type definition for Billing
interface BillingPauseResult {
  is_plan_pausable: boolean
  is_paused: boolean
  is_pausing_soon: boolean
  pause_date: string
  resume_date: string
  term_ends_date: string
}

// Result from /premium/undo_scheduled_plan_switch or /premium/undo_scheduled_cancellation
interface UndoScheduledChangeResult {
  success?: boolean
  errors?: string[]
}

type UndoLibraryScheduledChangeResult = UndoScheduledChangeResult

interface EstimateSwitchingCostResult {
  is_refund: boolean
  currency_code: string
  new_plan_cost: string
  unused_portion: string
  refund_amount: string
  amount_due: string
  invoice_amount: string
}

type EstimateLibrarySwitchingCostResult = EstimateSwitchingCostResult

interface ScheduledChangesResult {
  data: {
    has_scheduled_changes: boolean
  }
}
interface AdvertisedQuotasResult {
  neon: {
    max_upload_megabytes: number | null
    max_walls: number | null
  }
  gold: {
    max_upload_megabytes: number | null
    max_walls: number | null
  }
  platinum: {
    max_upload_megabytes: number | null
    max_walls: number | null
  }
}

interface AdvertisedQuotasApiResult {
  data: {
    neon: {
      max_upload_megabytes: number | null
      max_walls: number | null
    }
    gold: {
      max_upload_megabytes: number | null
      max_walls: number | null
    }
    platinum: {
      max_upload_megabytes: number | null
      max_walls: number | null
    }
  }
}

interface SwitchPlanResult {
  links: {
    status: string
  }
}

interface SwitchCurrencyResult {
  refund_successful: boolean
  switch_successful: boolean
}

type DockableModalState = 'expanded' | 'docked' | 'minimized'

// `type` should be name of action relative to root
interface DispatchableAction {
  type: string
  payload?: unknown
}

interface UploadedFile extends File {
  isGoogleDriveFile: boolean
}

interface PopoverAnchor {
  position?: 'top' | 'side' | 'bottom'
  top: number
  left: number
  width: number
  height?: number
}

type ActiveInputTag = 'section' | 'location' | 'postColor' | 'none'

interface LocationMenuPlaceInfo {
  id: string
  name: string
  longitude?: number
  latitude?: number
}

interface PostModalDropZone {
  left: number
  right: number
  top: number
  bottom: number
}

// TODO: Remove this information from Libraries when we have licenses service and API
interface LibraryUserQuotaInfo {
  advertizedStudentQuota: number
  advertizedTeacherQuota: number
  maxStudentQuota: number
  maxTeacherQuota: number
  studentQuotaHit: boolean
  studentQuotaUsed: number
  teacherQuotaHit: boolean
  teacherQuotaUsed: number
  ownerQuotaUsed: number
  maxOwnerQuota: number
}

interface Library {
  id: LibraryId
  ownerId: number
  ownerType: string
  name: string
  slug: string
  dashboardUrl: string
  avatar?: string
  defaultAvatar?: string
  bio?: string
  libraryType?: LibraryType
  privacy?: string
  version?: number
  createdAt?: string
  updatedAt?: string
  joinedAt?: string
  libraryAccess?: LibraryAccess
  addedMembershipsCount?: number
  nonLeftMembershipsCount?: number
  makersCount?: number
  membershipTier?: string
  quota: {
    wallsUsed: number
    wallsLimit: number
    quotaHit: boolean
    advertizedMaxUploadMegabytes: number
    advertizedWallsLimit: number
    maxUploadMegabytes: number
  }
  trialDaysLeft?: number
  isTrialBehaviorEnabled?: boolean
  isDowngradedFromPayingTier?: boolean
  isOwnerTeacherOrStaff?: boolean
  isPlanCancelled: boolean
  isTrialExpired?: boolean
  isDeprecatedFreeTier: boolean
  userQuota?: LibraryUserQuotaInfo
  visible?: boolean
}

interface UserLibrarySetting {
  libraryId: string
  avatar: string
  name: string
  namespace: string
  libraryType: LibraryType
  visible: boolean
}

type SchoolLibraryStudentPermission = Record<SchoolStudentPermissionBoolean, boolean> &
  Record<SchoolPermissionString, string>

type SchoolLibraryTeacherPermission = Record<SchoolTeacherPermissionBoolean, boolean> &
  Record<SchoolPermissionString, string>

interface SchoolLibraryPermission {
  settings: {
    student: SchoolLibraryStudentPermission
    teacher: SchoolLibraryTeacherPermission
  }
}

interface CanSetWallPrivacyOption {
  name: WallPrivacyOption
  canSet: boolean
}

interface CanSetWallPrivacyOptions {
  privacyOptions: CanSetWallPrivacyOption[]
}

type OwnerOnboardingTask = 'addNewTeam' | 'addMembers' | 'customizeTeamInfo' | 'makePrivateWall' | 'pickPlan'

interface OwnerOnboardingData {
  completedAt: string | null
  id: OnboardingFlowId
  libraryId: LibraryId
  tasks: Record<OwnerOnboardingTask, boolean | string>
}

interface OwnerOnboardingTaskData {
  key: OwnerOnboardingTask
  icon: string
  taskStringOne: string
  taskStringTwo: string
  taskButtonText: string
  taskButtonLabel: string
  isTaskDone: boolean
  taskDoneAt: string | null
  order: number
}

interface LibraryMembership {
  id: LibraryMembershipId
  libraryId: LibraryId
  userId: UserId
  role: LibraryMembershipRole
  status: 'added' | 'deactivated' | 'suspended' | 'left'
  user: Partial<User>
  updatedAt: string
  createdAt: string
}

interface LibraryUserEmailDomain {
  domainName: string

  // TODO: PRO-4890 update role to school role
  // should be Student or Teacher
  role: 'admin' | 'maker' | 'contributor'
}

interface LibraryAccess {
  canViewLibraryInfo: boolean
  canUpdateLibraryInfo: boolean
  canUpdatePermissions: boolean
  canViewLibraryMemberships: boolean
  canManageMembers: boolean
  canManageBilling: boolean
  canTrashLibrary: boolean
  canUseLibraryAsOwner: boolean
  canUseLibraryAsAdmin: boolean
  canUseLibraryAsTeacher: boolean
  canViewLibrarySecurity: boolean
  canInviteSchoolAdmins: boolean
  canInviteSchoolTeachers: boolean
  canInviteSchoolStudents: boolean
  canInviteMembers: boolean
  canViewGallery: boolean
  canManageContentSafetySettings: boolean
}

interface LibraryInviteLinks {
  id: Id
  linkForAdmin?: string
  linkForMaker?: string
  linkForContributor?: string
  linkForTeacher?: string
  linkForStudent?: string
  codeForAdminEnabled: boolean
  codeForMakerEnabled?: boolean
  codeForContributorEnabled?: boolean
  codeForTeacherEnabled?: boolean
  codeForStudentEnabled?: boolean
}

// Json Api
interface JsonApiResponse<T> {
  data?: JsonApiData<T> | Array<JsonApiData<T>>
  errors?: JsonApiError[]
  links?: JsonApiLinks
}

interface JsonApiData<T> {
  id: string
  type: string
  attributes: T
}

interface JsonApiError {
  status: string
  title: string
  detail: string
  code: string
  meta: {}
}

interface JsonApiLinks {
  next: string
}

interface WallCreationFromTemplateResult {
  url: string
}

// Wall Access
interface UserWallCollaborator {
  right: WallAccessRight
  type: 'user'
  id: UserId
  avatar: string
  email: string
  name: string
  username: string
  shortName?: string
  isRegistered: boolean
  status: WallCollaboratorStatus
}

interface UserGroupWallCollaborator {
  id: Id
  avatar: string
  name: string
  right?: WallAccessRight
  status?: WallCollaboratorStatus
  type: 'userGroup'
}

interface WallAccessSettings {
  wallId?: WallId
  privacyPolicyId?: PrivacyPolicyId
  privacyType: WallAccessPrivacyType
  right: WallAccessRight
  password: string | null
  collaborators: UserWallCollaborator[]
}

interface WallOnboardingPanel {
  wallId?: WallId
}

interface WallGalleryOnboardingData {
  id: string
  templateName: string
  templateDescription: string
  templatePadletUrl: string
  status: 'Internal' // If there are more potential statuses, you might want to expand this into a union type
  shouldCopyWallpaper: boolean
  postTypes: string // 'none' is the only value here, but if there are more possible values, this should be a union type
  previewPadletUrl: string
  samplePosts: number[][]
  templatePublicKey: string
  category: string[] // Assuming that this is an array of strings
  thumbnailUrl: string
  templateWallId: null // Assuming this is always null based on the provided object
  steps: Array<{
    note: string
    action: string | null // We should make this enum of possible values
  }>
}

interface WallLayout {
  name: string
  value: string
  image_path: {
    desktop: string
    mobile: string
  }
  template_url: string
  long_description: string
}

interface WallLayoutPreviewImage {
  name?: string
  path: string
  originalWidth: string
  originalHeight: string
}

interface WallLayoutAttributes {
  name: string
  value: string
  groupBy: 'section' | null
  icon: {
    path: string
    originalWidth: string
    originalHeight: string
  }
  preview: WallLayoutPreviewImage
  previewMobile: WallLayoutPreviewImage
  thumbnail: {
    path: string
    originalWidth: string
    originalHeight: string
  } | null
}

interface WallSectionBreakoutLink {
  wall_section_id: number
  link: string
}

interface WallSubmissionRequestProps {
  requires_login: boolean
  redirect_location: string
}

interface WallSubmissionRequestLink extends WallSubmissionRequestProps {
  link: string
}

interface SharedCollection {
  type: 'user' | 'library'
  avatar: string
  name: string
  id: Id
}

// Notifications

interface NotificationChannelInformation {
  notify: boolean
  mandatory: boolean
  available: boolean
}

type NotificationChannelList = Record<NotificationChannelType, NotificationChannelInformation>

interface NotificationSettingApiResponse {
  notification_type: NotificationType
  channels: NotificationChannelList
}

// User follows

interface UserFollowApiResponse {
  followerId: UserId
  user: Partial<User>
}

// Wall follows

interface WallFollowApiResponse {
  userId: UserId
  wall: Partial<Wall>
  follows: boolean
}

interface WallFollowInformation {
  current_user_follows_wall: boolean
  wall_follow_id: Id | null
  wall_follows_count: number
}

// Wall Read Token

interface WallReadTokenResponse {
  token: string
}

// Magic Walls

interface MagicWallCreationResult {
  url: string
}

interface MagicWallCreationStatusResult {
  status: string
  wall_url: string
}

// Google Drive Uploads

interface BeethovenUploadError {
  status: string
  title: string
  detail: string
  code: string
}
interface GoogleDriveUploadResponse {
  url: string
  errors?: BeethovenUploadError[]
}

// TODO: Replace with Vue-definied one after moving to Vue3.
type VueCssClass = string | Record<string, boolean> | VueCssClass[]

// Feed

interface PostFeedItemData {
  post: Post
  author: User
}

// Mentions

type UserMentionSuggestionAttributes = Required<
  Pick<UserWithHashId, 'id' | 'hashid' | 'display_name' | 'username' | 'avatar'>
> & { has_access?: boolean }
type UserMentionSuggestion = Pick<JsonAPIResource<UserMentionSuggestionAttributes>, 'type' | 'attributes'>
interface Command {
  name: string
  description: string
  icon: string
  level: CommandLevel
  action: Function
  filter?: Function
}

// Dashboard settings

interface UserLibrarySettingsApiResponse {
  defaultLibrary: UserLibrarySetting
  librarySettings: UserLibrarySetting[]
}

// Verification Code
type VerificationCodePurpose = 'change_email_address' | 'signup' | 'login'

interface EmailAddressVerificationResult {
  emailAddress: string
  status: string
  loginAsUserUrl?: string
}

// User groups

interface UserGroupFolderApiResponse {
  id: UserGroupId
  name: string
  wallIds: WallId[]
}

interface ContentModerationCategoryDetails {
  name: ContentModerationCategory
  enabled: boolean
  modifiable: boolean
}
interface ContentSafetySettings {
  contentModerationMode: ContentModerationMode
  moderationCategories: ContentModerationCategoryDetails[]
}

interface ContentModerationCategoryRequest {
  name: ContentModerationCategory
  enabled: boolean
}
interface ContentSafetySettingsUpdateApiRequest {
  contentModerationMode?: ContentModerationMode
  moderationCategories?: ContentModerationCategoryRequest[]
}

export type {
  AccountKey,
  AccountType,
  AccumulatedReactionData,
  AccumulatedReactions,
  ActiveInputTag,
  AdvertisedQuotasApiResult,
  AdvertisedQuotasResult,
  AttachmentProps,
  BackpackPlanPrices,
  BackpackPlanResult,
  // Google Drive Upload
  BeethovenUploadError,
  BillingPauseResult,
  CanSetWallPrivacyOption,
  CanSetWallPrivacyOptions,
  ChangeCardUrl,
  Cid,
  // Commands
  Command,
  Comment,
  CommentId,
  ContentModerationCategoryDetails,
  ContentSafetySettings,
  ContentSafetySettingsUpdateApiRequest,
  DeviceDetails,
  DispatchableAction,
  DockableModalState,
  DomainValidation,
  DraftPost,
  EmailAddressVerificationResult,
  EstimateCancellationRefundResult,
  EstimateLibrarySwitchingCostResult,
  EstimateSwitchingCostResult,
  // Libraries
  EstimateTeamNeonDowngradeRefund,
  FileAttributes,
  Folder,
  FolderId,
  GoogleDriveAuthorizationResult,
  GoogleDriveUploadResponse,
  Id,
  JsonApiData,
  JsonApiError,
  // Json Api
  JsonApiResponse,
  Library,
  LibraryAccess,
  LibraryDowngradeToTeamNeonResult,
  LibraryId,
  LibraryInviteLinks,
  LibraryInvoice,
  LibraryMembership,
  LibraryMembershipId,
  LibraryPlan,
  LibrarySwitchResult,
  LibrarySwitchStatusResult,
  LibraryUserEmailDomain,
  LibraryUserQuotaInfo,
  LocationMenuPlaceInfo,
  // Magic Wall
  MagicWallCreationResult,
  MagicWallCreationStatusResult,
  MapPostAttributes,
  // Notifications
  NotificationChannelInformation,
  NotificationChannelList,
  NotificationSettingApiResponse,
  OnboardingWallViz,
  OwnerOnboardingData,
  OwnerOnboardingTask,
  OwnerOnboardingTaskData,
  PinDropSuggestion,
  Plan,
  Point,
  PopoverAnchor,
  Post,
  PostAttributes,
  PostColor,
  PostConnection,
  // Feed
  PostFeedItemData,
  PostModalDropZone,
  PostPositionMap,
  // Wall Access
  PrivacyPolicyId,
  ReactionType,
  ScheduledChangesResult,
  SchoolLibraryPermission,
  SchoolLibraryStudentPermission,
  SchoolLibraryTeacherPermission,
  Section,
  SharedCollection,
  SharedCollectionAccountKey,
  SwitchCurrencyResult,
  SwitchPlanResult,
  Tenant,
  TenantAnalytics,
  TenantId,
  TenantInviteLinks,
  TenantInviteLinksParams,
  TenantParams,
  Theme,
  UndoLibraryScheduledChangeResult,
  UndoScheduledChangeResult,
  UpdateDraftAttributes,
  UpdatePostArgs,
  UploadedFile,
  User,
  UserCamelCase,
  // User follows
  UserFollowApiResponse,
  UserGroup,
  UserGroupFolderApiResponse,
  UserGroupId,
  // Others
  UserGroupWallCollaborator,
  UserId,
  UserLibrarySetting,
  // Dashboard settings
  UserLibrarySettingsApiResponse,
  UserMentionSuggestion,
  // Mentions
  UserMentionSuggestionAttributes,
  UserOptions,
  UserWallCollaborator,
  UserWithHashId,
  // Verification Code
  VerificationCodePurpose,
  // Vue
  VueCssClass,
  Wall,
  WallAccessSettings,
  WallBackground,
  WallCamelCase,
  // Templates
  WallCreationFromTemplateResult,
  WallCustomPostProperty,
  // Wall follows
  WallFollowApiResponse,
  WallFollowInformation,
  WallGalleryOnboardingData,
  WallGalleryTemplate,
  WallId,
  // Wall Layout
  WallLayout,
  WallLayoutAttributes,
  WallLayoutPreviewImage,
  WallOnboardingPanel,
  WallPostProperties,
  WallReactionData,
  WallReadTokenResponse,
  WallSectionBreakoutLink,
  WallSubmissionRequestLink,
  WallSubmissionRequestProps,
  WallTemplate,
  WallViz,
  WallVizNameType,
  WishContent,
}
